/**
 * The first thing somebody sees on a birthday.
 *
 * Held to the same rules as the rest of the full-screen moments: the person's
 * own palette behind them, transform and opacity only, one word-sized filter
 * tween on the top tier, plain fades under reduced motion.
 *
 * The drama is in the entrance rather than in effects — the name arrives
 * letter by letter out of blur, the room swells behind it, and a scatter of
 * light rises through the screen once and settles. Nothing loops: this is a
 * moment, not a wallpaper.
 */
import { useEffect, useMemo, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ArrowForwardRounded as NextIcon } from '@mui/icons-material';
import { animate, stagger } from 'animejs';
import { getMotionTier, softBlob } from '../common/motionTier';
import { readPalette } from '../onboarding/journey/paletteStore';
import { BASE_PALETTE } from '../onboarding/journey/journey.logic';
import { Occasion, alsoToday, occasionGreeting, occasionLine } from './occasion.logic';

const SERIF = "'Iowan Old Style','Palatino Linotype',Palatino,Georgia,serif";

const GLASS_TEXT = {
  background:
    'linear-gradient(150deg, color-mix(in srgb, var(--ink,#111827) 94%, transparent) 8%, color-mix(in srgb, var(--ink,#111827) 52%, transparent) 48%, color-mix(in srgb, var(--ink,#111827) 86%, transparent) 82%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
  filter: 'drop-shadow(0 1px 0 rgba(255,255,255,0.6)) drop-shadow(0 8px 18px rgba(15,23,42,0.18))',
} as const;

const BLOB_SLOTS = [
  { size: '74vmax', pos: { top: '-26%', left: '-22%' }, opacity: 0.9 },
  { size: '64vmax', pos: { bottom: '-32%', right: '-18%' }, opacity: 0.84 },
  { size: '54vmax', pos: { top: '18%', right: '0%' }, opacity: 0.72 },
  { size: '46vmax', pos: { bottom: '-6%', left: '2%' }, opacity: 0.6 },
] as const;

const SPARK_COUNT = 22;

export interface BirthdayMomentProps {
  occasions: Occasion[];
  firstName: string;
  userId?: number | string;
  onDone: () => void;
}

export default function BirthdayMoment({ occasions, firstName, userId, onDone }: BirthdayMomentProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const auroraRef = useRef<HTMLDivElement>(null);
  const sparkRef = useRef<HTMLDivElement>(null);

  const tier = useMemo(() => getMotionTier(), []);
  const still = tier === 'still';
  const palette = useMemo(() => readPalette(userId) || BASE_PALETTE, [userId]);
  const accent = palette[0];

  const lead = occasions[0];
  const greeting = occasionGreeting(lead, firstName);
  const also = alsoToday(occasions);

  // Sparks are placed once; they must not jump if the component re-renders.
  const sparks = useMemo(
    () => Array.from({ length: SPARK_COUNT }, (_, i) => ({
      left: `${4 + ((i * 37) % 92)}%`,
      size: 5 + (i % 4) * 4,
      delay: (i % 7) * 90,
      colour: palette[i % palette.length],
    })),
    [palette],
  );

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ── The room swells once, then breathes ────────────────────────────────
  useEffect(() => {
    if (still || !auroraRef.current) return;
    const blobs = auroraRef.current.querySelectorAll('[data-bd-blob]');
    const swell = animate(blobs, {
      scale: [0.8, 1.12, 1],
      duration: 2200,
      delay: stagger(90),
      ease: 'outElastic(1, .7)',
    });
    const drift = Array.from(blobs).map((b, i) =>
      animate(b, {
        translateX: [0, i % 2 === 0 ? 70 : -84, 0],
        translateY: [0, i % 2 === 0 ? -58 : 68, 0],
        duration: 17000 + i * 2600,
        ease: 'inOutSine',
        loop: true,
      }),
    );
    return () => { swell.cancel(); drift.forEach((a) => a.cancel()); };
  }, [still]);

  // ── The greeting arrives a letter at a time ────────────────────────────
  useEffect(() => {
    if (!rootRef.current) return;
    const letters = rootRef.current.querySelectorAll('[data-bd-letter]');
    const rest = rootRef.current.querySelectorAll('[data-bd-item]');
    if (still) {
      const a = animate([...Array.from(letters), ...Array.from(rest)], { opacity: [0, 1], duration: 260 });
      return () => { a.cancel(); };
    }
    const a1 = animate(letters, {
      opacity: [0, 1],
      translateY: [44, 0],
      scale: [1.2, 1],
      ...(tier === 'full' ? { filter: ['blur(18px)', 'blur(0px)'] } : {}),
      duration: 1200,
      delay: stagger(38, { start: 260 }),
      ease: 'outExpo',
    });
    const a2 = animate(rest, {
      opacity: [0, 1],
      translateY: [20, 0],
      duration: 850,
      delay: stagger(140, { start: 900 }),
      ease: 'outExpo',
    });
    return () => { a1.cancel(); a2.cancel(); };
  }, [still, tier, greeting]);

  // ── One rise of light, then gone. A loop would make it wallpaper. ──────
  useEffect(() => {
    if (still || !sparkRef.current) return;
    // Each spark drifts its own way, so they animate individually rather than
    // as one set: a function per-target would be neater, but anime's types
    // only take a function for a few fields and drift is not one of them.
    const anims = Array.from(sparkRef.current.querySelectorAll('[data-bd-spark]')).map((el, i) =>
      animate(el, {
        translateY: ['12vh', '-88vh'],
        translateX: [0, (i % 2 ? 1 : -1) * (18 + (i % 5) * 14)],
        opacity: [0, 0.85, 0],
        scale: [0.4, 1],
        duration: 3400,
        delay: 500 + i * 110,
        ease: 'outSine',
      }),
    );
    return () => anims.forEach((a) => a.cancel());
  }, [still]);

  return (
    <Box
      ref={rootRef}
      sx={{
        position: 'fixed', inset: 0, zIndex: 1600,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'var(--surface, #F8FAFC)' }} />
      <Box ref={auroraRef} sx={{ position: 'absolute', inset: 0 }}>
        {BLOB_SLOTS.map((slot, i) => (
          <Box
            key={i}
            sx={{ position: 'absolute', ...slot.pos, width: slot.size, height: slot.size, opacity: slot.opacity }}
          >
            <Box
              data-bd-blob
              sx={{
                width: '100%', height: '100%',
                background: softBlob(palette[i % palette.length]),
                willChange: 'transform',
              }}
            />
          </Box>
        ))}
      </Box>

      <Box ref={sparkRef} aria-hidden sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {sparks.map((s, i) => (
          <Box
            key={i}
            data-bd-spark
            sx={{
              position: 'absolute', bottom: 0, left: s.left,
              width: s.size, height: s.size, borderRadius: '50%',
              background: s.colour, opacity: 0, willChange: 'transform, opacity',
            }}
          />
        ))}
      </Box>

      <Box sx={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: 860, mx: 'auto', px: { xs: 3, sm: 5 } }}>
        <Typography
          data-bd-item
          sx={{
            fontSize: '0.74rem', letterSpacing: '0.2em', textTransform: 'uppercase',
            color: 'color-mix(in srgb, var(--ink,#111827) 52%, transparent)', mb: 2, opacity: 0,
          }}
        >
          {lead.kind === 'self' ? 'Today' : 'A date you asked us to remember'}
        </Typography>

        <Typography
          component="h1"
          sx={{
            fontFamily: SERIF, fontWeight: 500,
            fontSize: { xs: '2.3rem', sm: '3.6rem' },
            lineHeight: 1.05, letterSpacing: '-0.02em', mb: 2,
          }}
        >
          {/* Letters, not words: a name deserves to land one piece at a time.
              Spaces keep their own span so the line can still wrap. */}
          {Array.from(greeting).map((ch, i) => (
            <Box
              key={i}
              component="span"
              data-bd-letter
              sx={{
                ...GLASS_TEXT,
                display: 'inline-block',
                opacity: 0,
                whiteSpace: 'pre',
                willChange: 'transform, opacity',
              }}
            >
              {ch}
            </Box>
          ))}
        </Typography>

        <Typography
          data-bd-item
          sx={{
            fontFamily: SERIF, fontSize: { xs: '1.1rem', sm: '1.3rem' }, lineHeight: 1.5,
            maxWidth: 560, opacity: 0,
            color: 'color-mix(in srgb, var(--ink,#111827) 72%, transparent)',
          }}
        >
          {occasionLine(lead)}
        </Typography>

        {also && (
          <Typography
            data-bd-item
            sx={{
              mt: 1.5, fontSize: '0.98rem', opacity: 0,
              color: 'color-mix(in srgb, var(--ink,#111827) 58%, transparent)',
            }}
          >
            {also}
          </Typography>
        )}

        <Box data-bd-item sx={{ mt: 4, opacity: 0 }}>
          <Button
            onClick={onDone}
            endIcon={<NextIcon />}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: '1rem',
              borderRadius: '999px', px: 3.5, py: 1.25,
              color: 'var(--on-ink, #fff)', background: 'var(--ink, #111827)',
              boxShadow: `0 10px 26px ${accent}55`,
              transition: 'transform 180ms cubic-bezier(.2,.8,.2,1)',
              '&:hover': { background: 'var(--ink, #111827)', transform: 'translateY(-2px)' },
            }}
          >
            {lead.kind === 'self' ? 'Thank you' : 'Lovely'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
